import React, { PropsWithChildren, createContext, useContext, useRef } from 'react';

type MeasureFunc = () => undefined | number;

const Context = createContext<MeasureFunc>(() => window.innerHeight - 50);
Context.displayName = 'ContainerHeightContext';

/** A hook to get access to the container context */
export const useContainerHeight = () => useContext(Context);

/** This hook provides a ref and a way to measure the height. We could be smarter and trigger re-renders or use a ResizeObserver in the future, but for now this will on demand measure the height of the ref. */
export const useMeasureRef = (): [React.MutableRefObject<HTMLDivElement>, () => number] => {
	const ref = useRef<HTMLDivElement>();

	return [
		ref,
		() => {
			const height = ref.current?.getBoundingClientRect().height;
			return height;
		}
	];
};

/** This component makes it easy to reduce the available container space by a known measured item, which could be a toolbar. This should normally be used with the useConsumer hook. */
export const ConsumeContainer: React.FC<PropsWithChildren<{ measureConsumption: () => number }>> = ({ measureConsumption, children }) => {
	const measureContainer = useContainerHeight();
	return <Context.Provider value={() => measureContainer() - measureConsumption()}>{children}</Context.Provider>;
};

/** This Context should be used to measure a container to determine the available height. It could return undefined, if no container is provided or the container element is not yet mounted. */
export const ContainerHeightContext: React.FC<PropsWithChildren<{ measure: MeasureFunc }>> = ({ measure, children }) => {
	return <Context.Provider value={measure}>{children}</Context.Provider>;
};
